import { FC, Fragment, HTMLProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Dialog, DialogActions, List, ListItem, ListItemText, Stack } from '@mui/material';

import { Button, IconButton } from '@components/base';
import AppLogo from '@components/common/AtsLogo/AppLogo';
import env from '@config/runtimeConfig';
import { formatDate } from '@utils/dateUtils';

import { ActionButton } from './helperComponents';

const baseUrl = 'https://dev.azure.com/Altibox/Altibox TV Services/_git/altibox-tv-editor-frontend';

const prNumber = import.meta.env.VITE_GIT_LAST_COMMIT_MESSAGE?.match(/(PR )\d{4}/)?.[0]?.match(
  /\d+/
)?.[0];

const buildUrl = (path: string) => `${baseUrl}${path}`;

type IconButtonLinkProps = Pick<HTMLProps<HTMLAnchorElement>, 'href'>;

const IconButtonLink: FC<IconButtonLinkProps> = ({ href }) => (
  <IconButton component='a' fontSize='inherit' icon='openInNew' {...{ href, target: '_blank' }} />
);

const About: FC = () => {
  const { t } = useTranslation();

  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <Fragment>
      <ActionButton leftIcon='info' onClick={() => setDialogOpen(true)}>
        {t('about')}
      </ActionButton>

      <Dialog fullWidth maxWidth='sm' onClose={() => setDialogOpen(false)} open={dialogOpen}>
        <Box pt={2}>
          <AppLogo />
        </Box>

        <List dense>
          <ListItem
            secondaryAction={
              <IconButtonLink
                href={buildUrl(`?version=GB${import.meta.env.VITE_GIT_BRANCH_NAME}`)}
              />
            }
          >
            <ListItemText
              primary={t('about', { context: 'branch' })}
              secondary={import.meta.env.VITE_GIT_BRANCH_NAME}
            />
          </ListItem>

          <ListItem
            secondaryAction={
              <IconButtonLink href={buildUrl(`/commit/${import.meta.env.VITE_GIT_COMMIT_HASH}`)} />
            }
          >
            <ListItemText
              primary={t('about', { context: 'hash' })}
              secondary={import.meta.env.VITE_GIT_COMMIT_HASH}
            />
          </ListItem>

          <ListItem
            secondaryAction={
              prNumber ? <IconButtonLink href={buildUrl(`/pullrequest/${prNumber}`)} /> : null
            }
          >
            <ListItemText
              primary={t('about', { context: 'message' })}
              secondary={import.meta.env.VITE_GIT_LAST_COMMIT_MESSAGE}
            />
          </ListItem>

          <ListItem>
            <ListItemText
              primary={t('about', { context: 'date' })}
              secondary={formatDate(import.meta.env.VITE_GIT_COMMIT_DATE, 'full')}
            />
          </ListItem>

          <ListItem>
            <ListItemText
              primary={t('about', { context: 'env' })}
              secondary={t('env', { context: env })}
            />
          </ListItem>
        </List>

        <Stack spacing={2.5}></Stack>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('close')}</Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default About;
